import { LoggerService } from "@/shared/services";

import type { ColumnFiltersAction } from "./actions";
import {
  type ColumnFilter,
  type ColumnFilterMap,
  type ColumnMap,
  isExclusivityViolation,
} from "./state";

function newColumnFilter(stages: ColumnFilter["stages"]): ColumnFilter {
  return { stages, combinator: "or" };
}

/**
 * Applies one filter action to a column-filters map and returns the result.
 *
 * Filter edits reach a panel only as actions, and this is where each one is
 * interpreted, so the editing rules sit together: all-columns and per-column
 * filters are mutually exclusive, a stage index is clamped rather than left to
 * open a hole, and a column that loses its last stage loses its filter.
 *
 * `columns` is only read, and only to reject an edit naming a column that does
 * not exist. It comes from the panel's data rather than from the map being
 * edited (see `deriveLiveColumnFilters`), so nothing here can change it.
 *
 * A rejected action returns the `filters` it was given, by reference, so a
 * caller comparing by identity sees that nothing happened. Every other action
 * returns a fresh map, whether or not its contents differ.
 */
export function applyFilterAction(
  filters: ColumnFilterMap,
  action: ColumnFiltersAction,
  columns: ColumnMap,
): ColumnFilterMap {
  switch (action.type) {
    case "delete_filter": {
      const next = { ...filters };
      delete next[action.payload.columnId];
      return next;
    }
    case "set_filter": {
      // Design choice: __all__ (pan-column search) and per-column filters are
      // mutually exclusive. Users can search across all columns OR apply granular
      // per-column conditions with chained stages, but not both simultaneously.
      // Allowing both would significantly increase complexity (interaction between
      // global and column-specific match semantics, unclear highlighting behavior,
      // confusing filter pill layout) for a marginal use case. The dropdown in
      // FilterBar hides conflicting options and shows a helper message; this
      // guard is defense-in-depth.
      if (isExclusivityViolation(action.payload.columnId, filters)) {
        return filters;
      }
      if (!Object.hasOwn(columns, action.payload.columnId)) {
        LoggerService.warn(
          `set_filter: unknown column ID "${action.payload.columnId}"`,
        );
        return filters;
      }
      const next = { ...filters };
      const existing = filters[action.payload.columnId];
      const stages = existing ? [...existing.stages] : [];
      // undefined → append; otherwise clamp to prevent sparse array holes
      const idx = action.payload.stageIndex ?? stages.length;
      const clampedIndex = Math.min(idx, stages.length);
      stages[clampedIndex] = {
        value: action.payload.value,
        condition: action.payload.condition,
        ...(action.payload.value2 !== undefined && {
          value2: action.payload.value2,
        }),
      };
      next[action.payload.columnId] = existing
        ? { ...existing, stages }
        : newColumnFilter(stages);
      return next;
    }
    case "set_column_stages": {
      // Same guards as set_filter, in the same order: an edit that breaks
      // exclusivity is rejected before the column lookup, so it does not also
      // log an unknown-column warning.
      if (isExclusivityViolation(action.payload.columnId, filters)) {
        return filters;
      }
      if (!Object.hasOwn(columns, action.payload.columnId)) {
        LoggerService.warn(
          `set_column_stages: unknown column ID "${action.payload.columnId}"`,
        );
        return filters;
      }
      const next = { ...filters };
      // No stages means no filter, as when delete_stage removes the last one.
      if (action.payload.stages.length === 0) {
        delete next[action.payload.columnId];
        return next;
      }
      const existing = filters[action.payload.columnId];
      next[action.payload.columnId] = existing
        ? { ...existing, stages: action.payload.stages }
        : newColumnFilter(action.payload.stages);
      return next;
    }
    case "delete_stage": {
      // Remove a single stage; if the stages array becomes empty, remove the filter.
      const next = { ...filters };
      const existing = filters[action.payload.columnId];
      if (existing) {
        const stages = existing.stages.filter(
          (_, i) => i !== action.payload.stageIndex,
        );
        if (stages.length === 0) {
          delete next[action.payload.columnId];
        } else {
          next[action.payload.columnId] = { ...existing, stages };
        }
      }
      return next;
    }
    case "set_combinator": {
      const next = { ...filters };
      const existing = filters[action.payload.columnId];
      if (existing) {
        next[action.payload.columnId] = {
          ...existing,
          combinator: action.payload.combinator,
        };
      }
      return next;
    }
    case "clear_filters":
      return {};
    default: {
      const _exhaustive: never = action;
      return _exhaustive;
    }
  }
}
